import React from 'react'
import { useTranslation } from 'react-i18next'
import { KpiCard } from './KpiCard'
import { CreatedVsPrintedChart } from './CreatedVsPrintedChart'

type ProductionStageCard = {
    key: string
    label: string
    value: string
    unit: string
    note: string
    markets: Array<{ code: string; widthPct: number; value: string }>
}

type ProductionSectionProps = {
    summary: string | null
    stages: ProductionStageCard[]
    chart: {
        hasPartial: boolean
        yAxis: { ceiling: number; ticks: string[] }
        series: Array<{ label: string; created: number; printed: number; createdHeightPx: number; printedHeightPx: number }>
    }
}

export const ProductionSection: React.FC<ProductionSectionProps> = ({ summary, stages, chart }) => {
    const { t } = useTranslation('dashboard')
    return (
    <section className="flex flex-col gap-3">
        <div className="flex items-baseline gap-2.5 flex-wrap">
            <h2 className="text-[17px] font-semibold tracking-tight text-neutralBlack-900">{t('metrics.productionTitle')}</h2>
            {summary && <span className="text-[13px] text-neutralBlack-600">{summary}</span>}
        </div>

        {chart.series.length === 0 ? (
            <div className="bg-white rounded-2xl p-5 border border-neutralBlack-200 shadow-sm flex items-center justify-center h-[180px] text-sm text-neutralBlack-500">
                {t('metrics.productionEmpty')}
            </div>
        ) : (
            <div className="flex flex-col gap-1.5">
                <CreatedVsPrintedChart series={chart.series} yAxisTicks={chart.yAxis.ticks} />
                {chart.hasPartial && (
                    <span className="text-[11.5px] text-neutralBlack-500">{t('metrics.productionPartialNote')}</span>
                )}
            </div>
        )}

        <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(238px, 1fr))' }}>
            {stages.map((stage) => (
                <KpiCard key={stage.key} label={stage.label} value={stage.value} unit={stage.unit} note={stage.note} markets={stage.markets} />
            ))}
        </div>

        <p className="text-[12.5px] text-neutralBlack-400">
            {t('metrics.productionHelp')}
        </p>
    </section>
    )
}
